<script lang="ts" setup>
import { computed, ref } from 'vue';
import { DateTime } from 'luxon';
import { useLocalization } from '@/plugins/localization';
import { useThemeStore } from '@/store/theme';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { debounce } from 'lodash';

const model = defineModel<any|any[]>({
    get(value: any|any[])
    {
        if (value !== null && Array.isArray(value))
        {
            return value.map(p => toIso(p));
        }

        return toIso(value);
    },
    set(value: any|any[])
    {
        oldValue.value = model.value;

        if (value !== null && Array.isArray(value))
        {
            return value.map(p => toDateTime(p));
        }

        return toDateTime(value);
    }
});
const props = defineProps({
  "name": { default: '' },
  "type": { default: 'date' },
  "zone": { default: 'local' },
  "disabled": { type: Boolean, default: false },
  "minuteStep": { default: 5 },
  "placeholder": { default: null },
  "inline": { type: Boolean, default: false },
  "range": { type: Boolean, default: false },
  "multi": { type: Boolean, default: false },
  "preview": { type: Boolean, default: true },
  "position": { default: 'center' },
  "teleport": { type: Boolean, default: true },
  "maxDate": { default: null },
  "minDate": { default: null },
  "hideInputIcon": { type: Boolean, default: false },
  "weekNumbers": { type: Boolean, default: false }
});

const { $i18n } = useLocalization();
const store = useThemeStore();

const picker = ref(null);
const oldValue = ref(null);
const format = computed(() => props.type === 'datetime' ? 'yyyy-MM-dd HH:mm' : 'yyyy-MM-dd');
const locale = computed(() => $i18n.locale());
const timezone = computed(() => props.zone == 'UTC' || props.type == 'date' ? 'UTC' : undefined);
const timepicker = computed(() => props.type == 'datetime');
const dark = computed(() => store.theme == 'dark');
const maxDate = computed(() => props.maxDate && props.maxDate instanceof DateTime ? props.maxDate.toISO() : props.maxDate);
const minDate = computed(() => props.minDate && props.minDate instanceof DateTime ? props.minDate.toISO() : props.minDate);
const flow = computed<('month' | 'year' | 'calendar' | 'time' | 'minutes' | 'hours' | 'seconds')[]>(() => timepicker.value ? ['calendar', 'time'] : ['calendar']);
const utcPreserve = computed(() => timezone.value == 'UTC' ? 'preserve' : false);
const opened = defineModel<boolean>('opened', { default: false });

function clear()
{
    picker.value?.dispatchEvent(new CustomEvent('input', { bubbles: true }));
    opened.value = true;
    setTimeout(() => opened.value = false, 0);
}

function toIso(value: DateTime)
{
    return value instanceof DateTime ? value.toISO() : null;
}

function toDateTime(value: string)
{
    let date = value ? DateTime.fromISO(value).setZone('UTC') : null;

    if (props.type == 'date' && date !== null)
    {
        date = date.set({
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0
        });
    }

    return date;
}

function toIsoPreservingDate(d: Date)
{
    return DateTime.utc(
        d.getFullYear(),
        d.getMonth() + 1,
        d.getDate(),
        0, 0, 0, 0
    ).toISO();
}

const onSelect = debounce((val: any) =>
{

    if (timepicker.value)
        opened.value = val != null ? true : false;
    else
    {
        if (val === null)
            return;

        let newValue = null;

        if (val !== null && Array.isArray(val))
            newValue = val.map(d => toIsoPreservingDate(d));
        else
            newValue = toIsoPreservingDate(val);

        if (oldValue.value === newValue)
            return;

        opened.value = true;
        setTimeout(() => opened.value = false, 0);

        oldValue.value = model.value;
    }
}, 200);
</script>

<template>
    <div>
        <VueDatePicker
            v-model="model"
            model-type="iso"
            :timezone="timezone"
            :format="format"
            :locale="locale"
            :cancel-text="$t('[[[Anuluj]]]')"
            :select-text="$t('[[[Wybierz]]]')"
            :now-button-label="$t('[[[Teraz]]]')"
            :placeholder="props.placeholder"
            :enable-time-picker="timepicker"
            :minutes-increment="props.minuteStep"
            :minutes-grid-increment="props.minuteStep"
            :start-time="{minutes: Math.floor(DateTime.now().minute / 5) * 5}"
            :inline="props.inline"
            :range="props.range"
            :multi-calendars="props.multi"
            :dark="dark"
            :disabled="props.disabled"
            :auto-apply="true"
            :position="props.position"
            :teleport="props.teleport"
            :max-date="maxDate"
            :min-date="minDate"
            :flow="flow"
            :partial-flow="true"
            :hide-input-icon="props.hideInputIcon"
            :week-numbers="props.weekNumbers ? 'iso' : null"
            @cleared="clear"
            :utc="utcPreserve"
            @internal-model-change="onSelect"
        />
        <input type="hidden" :name="props.name" ref="picker">
        <div class="text-center small" v-if="model && props.preview && !timezone && !props.range">
            {{ $filters.date(model as string, format, 'UTC') }} (UTC)
        </div>
    </div>
</template>

<style scoped>
:deep(.dp__input) {
    margin-top: -1px;
    padding-top: 7px;
    padding-bottom: 7px;
    background-color: var(--bs-tertiary-bg);
}

:deep(.dp--menu-wrapper) {
    z-index: 1000;
}

:deep(.dp--tp-wrap) {
    max-width: 100%;
}
</style>
